// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, AlertTriangle, CalendarClock, ClipboardList, Sparkles, Stethoscope } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SnapshotView } from '@/components/SnapshotView'
import { usePatients } from '@/context/patient-store-context'
import { useAuth } from '@/context/auth-context'
import { isClinicalStaff } from '@/lib/access'
import { formatApptTime } from '@/lib/encounter'
import { usePatientDetail } from '@/hooks/use-patient-detail'
import { AppointmentCard, ClinicalPullsDrawer, PatientHeader, toKioskLang } from './PatientDetailPage'

// Dedicated, addressable page for ONE encounter/visit — so it is unambiguous which
// visit an intake belongs to. Reuses the same AppointmentCard the Encounters list
// renders (intake actions, transcript, this encounter's prep note) plus the patient
// Snapshot, scoped to the appointmentId in the URL.
export default function EncounterDetailPage() {
  const { t } = useTranslation()
  const { id, appointmentId } = useParams<{ id: string; appointmentId: string }>()
  const navigate = useNavigate()
  const { updatePatient } = usePatients()
  const { role } = useAuth()

  // Shared hook: same fetch + eventual-consistency catch-up polling as PatientDetailPage,
  // so a note/enrichment/clinical pull that lands after hand-off surfaces here with no
  // manual reload.
  const {
    patient,
    apiPatient,
    fetchStatus: status,
    reload,
    refreshRecords,
    syncing,
    catchingUp,
  } = usePatientDetail(id)
  const [localReviewed, setLocalReviewed] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)

  const backToEncounters = useCallback(() => navigate(`/patients/${id}?tab=encounters`), [navigate, id])

  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
        <span className="h-10 w-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin-ring" />
        <p className="text-sm text-muted-foreground">{t('patientDetail.loading', 'Loading…')}</p>
      </div>
    )
  }

  const appt = apiPatient?.appointments?.find((a) => a.appointmentId === appointmentId)

  if (status === 'notfound' || (status === 'done' && (!patient || !appt))) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
        <div className="h-16 w-16 rounded-2xl bg-secondary flex items-center justify-center">
          <CalendarClock className="h-8 w-8 text-muted-foreground" />
        </div>
        <div>
          <p className="text-xl font-bold text-foreground">{t('encounterDetail.notFound', 'Encounter not found')}</p>
          <p className="text-sm text-muted-foreground mt-1">{t('encounterDetail.notFoundBody', "This visit may have been removed, or the link is out of date.")}</p>
        </div>
        <Button variant="outline" onClick={backToEncounters}>
          <ArrowLeft className="h-4 w-4" />
          {t('encounterDetail.backToEncounters', 'Back to visits')}
        </Button>
      </div>
    )
  }

  if (status === 'error' || !patient || !apiPatient || !appt) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
        <div className="h-16 w-16 rounded-2xl bg-destructive/10 flex items-center justify-center">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>
        <p className="text-xl font-bold text-foreground">{t('patientDetail.loadFailed', 'Could not load encounter')}</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={backToEncounters}>{t('patientDetail.goBack', 'Go back')}</Button>
          <Button onClick={() => reload()}>{t('patientDetail.retry', 'Retry')}</Button>
        </div>
      </div>
    )
  }

  const prepStatus = localReviewed ? 'reviewed' : patient.prepStatus
  const slot = formatApptTime(appt.scheduledAt, appt.clinicTimezone)

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 pb-6">
      <div className="flex flex-col gap-3">
        <Link
          to={`/patients/${id}?tab=encounters`}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors w-fit"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('encounterDetail.backToEncounters', 'Back to visits')}
        </Link>
      </div>

      <PatientHeader
        patient={patient}
        apiPatient={apiPatient}
        prepStatus={prepStatus}
        syncing={syncing}
        catchingUp={catchingUp}
        onRefreshRecords={() => void refreshRecords()}
        onOpenHistory={() => setHistoryOpen(true)}
      />

      <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <dl className="grid gap-px bg-border sm:grid-cols-3">
          <div className="bg-card px-4 py-3">
            <dt className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <ClipboardList className="h-3.5 w-3.5 text-primary" />
              {t('patientDetail.reasonForVisit')}
            </dt>
            <dd className="mt-1.5 text-sm font-semibold text-foreground">{appt.reason || '—'}</dd>
          </div>
          <div className="bg-card px-4 py-3">
            <dt className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <Stethoscope className="h-3.5 w-3.5 text-primary" />
              {t('patientDetail.labelProvider')}
            </dt>
            <dd className="mt-1.5 text-sm font-semibold text-foreground">{appt.providerName || '—'}</dd>
          </div>
          <div className="bg-card px-4 py-3">
            <dt className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <CalendarClock className="h-3.5 w-3.5 text-primary" />
              {t('encounterDetail.appointment', 'Appointment')}
            </dt>
            <dd className="mt-1.5 text-sm font-semibold text-foreground">
              {[slot.date, slot.time].filter(Boolean).join(' · ') || '—'}
            </dd>
          </div>
        </dl>
      </section>

      <div className="flex items-center gap-2 px-1">
        <ClipboardList className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold text-foreground">
          {t('encounterDetail.intakeWorkflow', 'Intake workflow')}
        </h2>
      </div>

      {/* The single encounter — same card the list uses, scoped to this appointment.
          Landing here from Start Intake should be ready to go: the kiosk hand-off
          opens immediately for a not-yet-sent intake (with Send Self-Eval Link
          offered alongside). */}
      <AppointmentCard
        appt={appt}
        patient={patient}
        prepStatus={prepStatus}
        defaultLanguage={toKioskLang(apiPatient?.language)}
        defaultShowKiosk
        showSummary={false}
        onReview={() => {
          setLocalReviewed(true)
          updatePatient(patient.id, { prepStatus: 'reviewed' })
        }}
        onRefresh={() => reload()}
      />

      {/* Patient snapshot (transparency/QA) — an operator/QA surface, not part of
          the clinical workflow, so clinical staff never see it. */}
      {!isClinicalStaff(role) && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" />
            <h2 className="text-sm font-semibold text-foreground">{t('patientDetail.tabSnapshot', 'Snapshot')}</h2>
          </div>
          <SnapshotView patientId={patient.id} />
        </section>
      )}

      <ClinicalPullsDrawer
        patientId={patient.id}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  )
}
